import React, { createContext, useContext, ReactNode } from 'react';
import { generateDatadogTrackingProps } from 'toolkit/environment/environment/user-monitoring';

export interface TrackingContextValue {
  generateTrackingProps: (actionName: string) => Record<string, string>;
  getFullContextName: () => string;
}

const TrackingContext = createContext<TrackingContextValue | null>(null);

interface TrackingProviderProps {
  children: ReactNode;
  contextName: string;
}

export const TrackingProvider: React.FC<TrackingProviderProps> = ({
  children,
  contextName,
}) => {
  const parentContext = useContext(TrackingContext);

  const getFullContextName = (): string => {
    if (parentContext) {
      return `${parentContext.getFullContextName()}.${contextName}`;
    }
    return contextName;
  };

  const generateTrackingProps = (
    actionName: string
  ): Record<string, string> => {
    const fullContextName = getFullContextName();
    const fullActionName = `${fullContextName}.${actionName}`;

    return generateDatadogTrackingProps(fullActionName);
  };

  const contextValue: TrackingContextValue = {
    generateTrackingProps,
    getFullContextName,
  };

  return (
    <TrackingContext.Provider value={contextValue}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracking = (): TrackingContextValue => {
  const context = useContext(TrackingContext);
  if (!context) {
    // Return a no-op implementation instead of throwing
    // This allows components to work even without explicit tracking context
    return {
      generateTrackingProps: () => ({}),
      getFullContextName: () => 'no-context',
    };
  }
  return context;
};
